import { Alert, AlertTitle, Stack, Switch, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useState } from "react";
import { useFragment } from "react-relay";
import { LiquidGlassCard_brand$key } from "./__generated__/LiquidGlassCard_brand.graphql";
import CardSection from "../../../../components/CardSection";
import LeftRight from "../../../../components/LeftRight";
import SaveButton, { SavedState } from "../../../../components/SaveButton";
import {
  ReducerAction,
  useMobilePreviewDispatch,
} from "../../../../contexts/MobilePreviewContext";
import useBrandAppConfigUpdateMutation from "../../../../mutations/useBrandAppConfigUpdateMutation";

const brandFragment = graphql`
  fragment LiquidGlassCard_brand on BrandType {
    appConfig {
      id
      buildTheme {
        id
        iosLiquidGlassEnabled
        iosLiquidGlassRequiresNewBuild
      }
    }
  }
`;

type Props = {
  brand: LiquidGlassCard_brand$key;
};

const LiquidGlassCard = ({ brand: brandKey }: Props) => {
  const dispatch = useMobilePreviewDispatch();
  const brand = useFragment<LiquidGlassCard_brand$key>(brandFragment, brandKey);
  const buildTheme = brand.appConfig.buildTheme;

  const [iosLiquidGlassEnabled, setIosLiquidGlassEnabled] = useState(
    buildTheme.iosLiquidGlassEnabled
  );
  const [saveButtonState, setSaveButtonState] = useState<SavedState>(
    SavedState.SAVED
  );

  const onToggleChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setIosLiquidGlassEnabled(checked);
      dispatch({
        type: ReducerAction.UPDATE_THEME,
        payload: {
          theme: {
            iosLiquidGlassEnabled: checked,
          },
        },
      });
      saveButtonState !== SavedState.ENABLED &&
        setSaveButtonState(SavedState.ENABLED);
    },
    [dispatch, saveButtonState]
  );

  const [saveMutation, isMutationInFlight] = useBrandAppConfigUpdateMutation(
    {
      id: brand.appConfig.id,
      buildTheme: {
        id: brand.appConfig.buildTheme.id,
        iosLiquidGlassEnabled,
      },
    },
    () => {
      setSaveButtonState(SavedState.SAVED);
    }
  );

  return (
    <CardSection
      actions={
        <SaveButton
          savedState={
            isMutationInFlight ? SavedState.DISABLED : saveButtonState
          }
          onClick={saveMutation}
        />
      }
      title="Liquid Glass (iOS 26+)"
      subtitle="Enable liquid glass design on navigation elements."
      content={
        <Stack spacing={2} sx={{ maxWidth: "650px" }}>
          {buildTheme.iosLiquidGlassRequiresNewBuild && (
            <Alert severity="warning" sx={{ width: "100%" }}>
              <AlertTitle sx={{ fontWeight: "bold" }}>
                Requires a new build
              </AlertTitle>
              Enabling liquid glass initially requires a new app submission to
              Apple. Disabling liquid glass is immediate. Liquid glass will only
              appear on eligible devices running iOS 26 or later. Devices
              running older versions of iOS will continue to see the same design
              as Android devices.
            </Alert>
          )}
          <LeftRight
            expandLeft={true}
            left={
              <Stack>
                <Typography
                  variant="body2"
                  sx={{
                    color: "black",
                    fontWeight: "bold",
                  }}
                >
                  Enable Liquid Glass
                </Typography>
                <Typography sx={{ paddingBottom: "16px" }} variant="body2">
                  Use the preview selector to compare the iOS and Android
                  designs.
                </Typography>
              </Stack>
            }
            right={
              <Switch
                checked={iosLiquidGlassEnabled}
                onChange={onToggleChange}
              />
            }
          />
        </Stack>
      }
    />
  );
};

export default LiquidGlassCard;
